"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Instance } from "flatpickr";
import { getFlatpickrLocale } from "@/utils/flatpickrLocale";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface BirthdateFieldProps {
  birthdate: Date | undefined;
  error: string;
  disabled?: boolean;
  onBirthdateChange: (date: Date | undefined) => void;
}

const BirthdateField: React.FC<BirthdateFieldProps> = ({
  birthdate,
  error,
  disabled = false,
  onBirthdateChange,
}) => {
  const { t, i18n } = useTranslation();
  const fpInstanceRef = React.useRef<Instance | null>(null);

  const flatpickrOptions = React.useMemo(() => {
    const today = new Date();
    const minDate = new Date(1900, 0, 1);
    return {
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "d.m.Y",
      maxDate: today,
      minDate,
      locale: getFlatpickrLocale(i18n.language),
      disableMobile: true,
      allowInput: true,
      clickOpens: false
    };
  }, [i18n.language]);

  return (
    <div className="space-y-2">
      <label htmlFor="birthdate" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
        {t("contactDetails.birthdate")}
      </label>
      <div className="relative">
        <Flatpickr
          key={i18n.language}
          value={birthdate}
          options={flatpickrOptions}
          onChange={(dates: Date[]) => onBirthdateChange(dates[0] ?? undefined)}
          onReady={(_, __, instance) => {
            fpInstanceRef.current = instance;
          }}
          render={(fpProps, ref) => {
            const { defaultValue } = fpProps as { defaultValue?: string };
            return (
              <Input
                id="birthdate"
                placeholder={t("common.datePlaceholder")}
                disabled={disabled}
                className={cn("pr-10", error && "border-destructive focus-visible:ring-destructive")}
                defaultValue={defaultValue}
                ref={ref as React.Ref<HTMLInputElement>}
              />
            );
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
          onClick={() => fpInstanceRef.current?.open()}
        >
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="sr-only">
            {t("common.openCalendar", { defaultValue: "Kalender öffnen" })}
          </span>
        </Button>
      </div>
      <p className="text-xs text-muted-primary">
        {t("pclosing-pet-parent-birthdate-explanation")}
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default BirthdateField;
